import { useNode } from "@craftjs/core";
import React, { useEffect, useMemo } from "react";
import FroalaEditorView from "react-froala-wysiwyg/FroalaEditorView";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import useApplyGlobalSettings, {
  CODEBLOCK_TYPES,
} from "../../../../hooks/useApplyGlobalSettings";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import { ASSETS } from "../../../../utils/config";
import { sanitizeUserHtml } from "../../../../utils/sanitizeHtml";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import ImageBlock from "../../Shared/ImageBlock";
import "../Text/Text.scss";
import ImageWithTextSettings from "./ImageWithTextSettings";
import { getSelectedSegment } from "../../../../redux/selectors/segmentBuilderSelectors";
import { useDispatch, useSelector } from "react-redux";
import { updateImageWithTextStyles } from "../../../../redux/reducers/PrevStylesCopy/CodeblockDefaultStyles";

const ImageContainer = styled.div`
  padding: ${(props) => props.padding};
  display: ${(props) =>
    props.textPosition === "top" || props.textPosition === "bottom"
      ? "flex"
      : "block"};
  flex-direction: ${(props) =>
    props.textPosition === "top" ? "column" : "column-reverse"};
  align-items: ${(props) =>
    props.textPosition === "top" || props.textPosition === "bottom"
      ? "center"
      : "unset"};
  justify-content: ${(props) =>
    props.textPosition === "top" || props.textPosition === "bottom"
      ? "center"
      : "unset"};
  overflow: hidden; /* Ensure content remains within the container */
`;

const TextBlockContainer = styled.div`
  color: #123051;
  font-size: 14px;
  font-weight: 400;
`;

const TextContainer = styled.div`
  background-color: #344054;
  padding: 8px;
`;

export const ImageWithText = ({
  src,
  alt = "Image with text",
  width = "50%",
  isApplyMarginOnAllSides = false,
  allSideMargin = 5,
  margin = { top: 5, bottom: 5, left: 10, right: 10 },
  borderRadius = 4,
  borderColor = "suggested_color_0",
  borderWidth = 1,
  showImageTitle = false,
  title = "",
  hyperlink = null,
  showHyperlink = false,
  dropShadowColor = null,
  textPosition = "right",
  text = "",
  image = {},
  defaultImage = ASSETS ? ASSETS.segmentEditor.UploadImage : "",
  headingProps = {
    fontColor: "black",
    fontFamily: "Poppins",
    fontSize: 16,
    resetKey: 0,
  },
  paragraphProps = {
    fontColor: "black",
    fontFamily: "Poppins",
    fontSize: 14,
    resetKey: 0,
  },
  lineHeight = 1.25,
  isLoading = false,
}) => {
  const {
    connectors: { connect, drag },
    actions: { setProp },
  } = useNode();
  const location = useLocation();
  const dispatch = useDispatch();
  const { imageWithText: imageWithTextProps } = useSelector(
    (state) => state.codeblockStyles
  );

  useApplyGlobalSettings(setProp, CODEBLOCK_TYPES.ImageWithText);

  const selectedSegment = useSelector(getSelectedSegment);
  const selectedSegmentColor = selectedSegment?.settings?.color;
  const { getPaletteColor } = usePaletteColor();

  const hasWheelInURL = location.pathname.toLowerCase().includes("wheel");

  const marginStyle = isApplyMarginOnAllSides
    ? `${allSideMargin}px`
    : margin
    ? `${margin?.top}px ${margin?.right}px ${margin?.bottom}px ${margin?.left}px`
    : undefined;

  const border = borderColor
    ? `${borderWidth}px solid ${getPaletteColor(borderColor)}`
    : "none";

  const imageSelected = image?.src || src;

  const ImageBlockStyle = {
    cursor: showHyperlink ? "pointer" : "default",
    boxShadow:
      dropShadowColor &&
      `0px 0px 9.3px 0px ${getPaletteColor(dropShadowColor)}`,
    borderRadius,
    width: width,
    maxHeight: "100%", // Ensure image doesn't exceed the container's height
    height: imageSelected ? "auto" : "130px", // Auto-adjust height based on content
    float: textPosition === "left" ? "right" : "left",
    margin:
      textPosition === "left"
        ? "0 0 8px 8px"
        : text.includes("<ul>") || text.includes("<ol>")
        ? "0 20px 8px 0px"
        : "0 20px 8px 0",
    border,
    objectFit: "contain", // Maintain aspect ratio and ensure image fits inside the container
  };

  const froalaCssVariables = useMemo(() => {
    const { fontColor: hc, fontFamily: hf, fontSize: hs } = headingProps;
    const { fontColor: pc, fontFamily: pf, fontSize: ps } = paragraphProps;
    return {
      "--heading-color": hc || "black",
      "--heading-family": hf || "Poppins, sans-serif",
      "--heading-size": `${hs || 16}px`,
      "--paragraph-color": pc || "black",
      "--paragraph-family": pf || "Poppins, sans-serif",
      "--paragraph-size": `${ps || 14}px`,
      "--line-height": lineHeight,
    };
  }, [headingProps, paragraphProps, lineHeight]);

  useEffect(() => {
    if (!borderColor) {
      const timeoutId = setTimeout(() => {
        setProp((props) => {
          props.borderColor = "suggested_color_0";
        });
      }, 10);

      return () => clearTimeout(timeoutId);
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setProp((props) => {
        // Initialize image if it's undefined
        props.image = props.image || {};

        // Don't copy text and image content values, only styles
        props.width = props.width || imageWithTextProps.width;
        props.isApplyMarginOnAllSides =
          props.isApplyMarginOnAllSides ||
          imageWithTextProps.isApplyMarginOnAllSides;
        props.margin = props.margin || { ...imageWithTextProps.margin };
        props.allSideMargin =
          props.allSideMargin || imageWithTextProps.allSideMargin;
        props.borderRadius =
          props.borderRadius || imageWithTextProps.borderRadius;
        props.borderWidth = props.borderWidth || imageWithTextProps.borderWidth;
        props.borderColor = props.borderColor || imageWithTextProps.borderColor;
        props.showImageTitle =
          props.showImageTitle || imageWithTextProps.showImageTitle;
        props.showHyperlink =
          props.showHyperlink || imageWithTextProps.showHyperlink;
        props.dropShadowColor =
          props.dropShadowColor || imageWithTextProps.dropShadowColor;
        props.textPosition =
          props.textPosition || imageWithTextProps.textPosition;
        props.headingProps = props.headingProps || {
          ...imageWithTextProps.headingProps,
        };
        props.paragraphProps = props.paragraphProps || {
          ...imageWithTextProps.paragraphProps,
        };
        props.lineHeight = props.lineHeight || imageWithTextProps.lineHeight;
        props.defaultImage =
          props.defaultImage ||
          (ASSETS ? ASSETS.segmentEditor.UploadImage : "");
      });
    }, 5);

    // Cleanup timeout if the component unmounts or dependencies change
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div ref={(ref) => connect(drag(ref))}>
      <BlockIndicator>
        <ImageContainer padding={marginStyle} textPosition={textPosition}>
          <ImageBlock
            src={image?.src || src || defaultImage}
            title={image?.title || title}
            showImageTitle={showImageTitle}
            showHyperlink={showHyperlink}
            hyperlink={hyperlink}
            isImageLoading={isLoading}
            style={ImageBlockStyle}
            imageStyle={{
              height: !imageSelected ? "100%" : "auto",
              objectFit: !imageSelected ? "cover" : "contain",
            }}
          />

          <TextBlockContainer>
            <div className="froalaEditor" style={froalaCssVariables}>
              <FroalaEditorView
                model={sanitizeUserHtml(
                  text ||
                    (!hasWheelInURL
                      ? '<span style="color: grey;">Add Text Here</span>'
                      : "")
                )}
              />
            </div>
          </TextBlockContainer>
        </ImageContainer>
      </BlockIndicator>
    </div>
  );
};

export const ImageWithTextDefaultProps = {
  isLoading: false,
};

ImageWithText.craft = {
  props: ImageWithTextDefaultProps,
  related: {
    settings: ImageWithTextSettings,
  },
};
